import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, X } from "lucide-react";
import { getSocialLinks, getSocialPlatforms, saveSocialLinks, deactivateSocialLink } from "../../services/roomService";
import Loader from "../common/Loader";
import Button from "../common/Button";
import { color, radius } from "../../styles/theme";

const PLATFORM_KEYS = ["website", "x", "youtube", "facebook", "instagram", "tiktok", "twitch"];

/** Per-platform URL validation, matching mobile's a-room-social-link screen exactly. */
const isValidPlatformUrl = (url, platform) => {
  if (!url?.trim()) return true; // empty is fine, just means "not set"
  const lower = url.toLowerCase();
  switch (platform) {
    case "youtube": return lower.includes("youtube.com") || lower.includes("youtu.be");
    case "instagram": return lower.includes("instagram.com");
    case "facebook": return lower.includes("facebook.com") || lower.includes("fb.com");
    case "twitch": return lower.includes("twitch.tv");
    case "x": return lower.includes("x.com") || lower.includes("twitter.com");
    case "website": return lower.startsWith("http://") || lower.startsWith("https://");
    case "tiktok": return lower.includes("tiktok.com");
    default: return false;
  }
};

/**
 * Social Links editor — ported from mobile's a-room-social-link screen
 * (src/Screens/a-room-social-link). Account-level, like Promo Image — not
 * per-room.
 */
const SocialLinksEditor = () => {
  const { t } = useTranslation(["rooms", "common"]);
  const navigate = useNavigate();
  const PLATFORMS = PLATFORM_KEYS.map((key) => ({ key, label: t(`socialLinksEditor.platforms.${key}`) }));
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]); // catalog: [{Id, Code}]
  const [links, setLinks] = useState({}); // { [code]: url }
  const [saving, setSaving] = useState(false);
  const [removingKey, setRemovingKey] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([getSocialPlatforms(), getSocialLinks()])
      .then(([platformsRes, linksRes]) => {
        const platformList = Array.isArray(platformsRes.data) ? platformsRes.data : platformsRes.data?.Result || [];
        setPlatforms(platformList);

        const linkList = Array.isArray(linksRes.data) ? linksRes.data : linksRes.data?.Result || [];
        const map = {};
        linkList.forEach((l) => { if (l?.platformCode) map[l.platformCode] = l.Url || ""; });
        setLinks(map);
      })
      .catch(() => toast.error(t("socialLinksEditor.toasts.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleChange = (key, value) => setLinks((prev) => ({ ...prev, [key]: value }));

  const handleRemove = async (key) => {
    const platformObj = platforms.find((p) => p.Code === key);
    if (!platformObj?.Id) {
      setLinks((prev) => ({ ...prev, [key]: "" }));
      return;
    }
    setRemovingKey(key);
    try {
      const res = await deactivateSocialLink(platformObj.Id);
      toast.success(res.data?.Message || t("socialLinksEditor.toasts.linkRemoved"));
      setLinks((prev) => ({ ...prev, [key]: "" }));
    } catch (err) {
      toast.error(err.response?.data?.Message || t("socialLinksEditor.toasts.removeFailed"));
    } finally {
      setRemovingKey(null);
    }
  };

  const handleSave = async () => {
    for (const { key, label } of PLATFORMS) {
      if (!isValidPlatformUrl(links[key], key)) {
        toast.error(t("socialLinksEditor.toasts.invalidUrl", { platform: label }));
        return;
      }
    }

    const payload = PLATFORMS.reduce((acc, { key }) => {
      const url = links[key]?.trim();
      if (!url) return acc;
      const platformObj = platforms.find((p) => p.Code === key);
      if (platformObj?.Id) acc.push({ socialPlatformId: platformObj.Id, url });
      return acc;
    }, []);

    if (payload.length === 0) {
      toast.info(t("socialLinksEditor.toasts.addAtLeastOne"));
      return;
    }

    setSaving(true);
    try {
      const res = await saveSocialLinks(payload);
      toast.success(res.data?.Message || t("socialLinksEditor.toasts.linksSaved"));
    } catch (err) {
      toast.error(err.response?.data?.Message || t("socialLinksEditor.toasts.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate(-1)}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("socialLinksEditor.backButton")}
      </button>
      <h2 style={s.title}>{t("socialLinksEditor.title")}</h2>
      <div style={s.wrap}>
        <p style={s.desc}>{t("socialLinksEditor.description")}</p>

        {PLATFORMS.map(({ key, label }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={s.fieldLabel}>{label}</label>
            <div style={s.inputRow}>
              <input
                type="text"
                className="input"
                value={links[key] || ""}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={t("socialLinksEditor.urlPlaceholder")}
              />
              {links[key] && (
                <button type="button" className="btn btn-danger-ghost btn-sm" style={{ ...s.removeBtn, border: "1.5px solid #fecaca" }} onClick={() => handleRemove(key)} disabled={removingKey === key}>
                  {removingKey === key ? t("socialLinksEditor.removing") : <X size={14} strokeWidth={2.25} />}
                </button>
              )}
            </div>
          </div>
        ))}

        <Button onClick={handleSave} disabled={saving} style={{ marginTop: 8 }}>
          {saving ? t("socialLinksEditor.saving") : t("socialLinksEditor.saveButton")}
        </Button>
      </div>
    </div>
  );
};

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, marginBottom: 8, padding: "6px 10px" },
  title: { fontSize: 19, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  wrap: { maxWidth: 460 },
  desc: { fontSize: 12.5, color: color.textMuted, marginBottom: 22, lineHeight: 1.5 },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  inputRow: { display: "flex", gap: 8 },
  removeBtn: { width: 40, flexShrink: 0, padding: 0, justifyContent: "center" },
};

export default SocialLinksEditor;
